import { 
  collection, 
  addDoc,
  updateDoc, 
  doc, 
  getDocs, 
  getDoc, 
  query, 
  where, 
  orderBy,
  limit
} from "firebase/firestore";
import { db } from "./firebase";
import { bookingActionsService } from "./bookingActionsService";
import { Outing, Booking } from "../features/jacana/types";
import { e2eJacanaMock } from "../utils/e2eJacanaMock";

const OUTINGS_COLLECTION = "outings";
const BOOKINGS_COLLECTION = "bookings";

function toMillis(value: unknown): number {
  if (!value) return 0;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  if (typeof value === 'object' && value !== null && 'toMillis' in value && typeof (value as { toMillis: () => number }).toMillis === 'function') {
    return (value as { toMillis: () => number }).toMillis();
  }
  return 0;
}

function sortBookingsByCreatedAt(bookings: Booking[]) {
  return bookings.sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));
}

export const outingsService = {
  async getAll(options: { includeDeleted?: boolean } = {}) {
    if (e2eJacanaMock.isEnabled()) {
      const allOutings = e2eJacanaMock.getAllOutings();
      return options.includeDeleted ? allOutings : allOutings.filter(outing => !(outing as any).deleted);
    }

    const q = query(
      collection(db, OUTINGS_COLLECTION), 
      orderBy("date", "asc")
    );
    const snapshot = await getDocs(q);
    const outings = snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() } as Outing));

    return options.includeDeleted ? outings : outings.filter(outing => !(outing as any).deleted);
  },

  async getById(id: string) {
    if (e2eJacanaMock.isEnabled()) {
      return e2eJacanaMock.getOutingById(id);
    }

    const docRef = doc(db, OUTINGS_COLLECTION, id);
    const snapshot = await getDoc(docRef);
    if (!snapshot.exists()) return null;
    return { id: snapshot.id, ...snapshot.data() } as Outing;
  },

  async create(outing: Omit<Outing, 'id'>) {
    const docRef = await addDoc(collection(db, OUTINGS_COLLECTION), {
      ...outing,
      deleted: false
    });
    return docRef.id;
  },

  async update(id: string, data: Partial<Outing>) {
    const docRef = doc(db, OUTINGS_COLLECTION, id);
    await updateDoc(docRef, data);
  },

  async delete(id: string) {
    const docRef = doc(db, OUTINGS_COLLECTION, id);
    await updateDoc(docRef, { deleted: true });
  },

  async cancelOuting(id: string, reason = 'organisation_cancellation', organizerMessage?: string) {
    const bookingsCol = collection(db, BOOKINGS_COLLECTION);
    const q = query(bookingsCol, where("outingId", "==", id));
    const snapshot = await getDocs(q);
    const impactedBookings = snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() } as Booking));

    await bookingActionsService.cancelOutingAndCascade({
      outingId: id,
      reason,
      sendEmails: false,
      organizerMessage,
    });

    return impactedBookings;
  },

  async getPastOutings(limitCount: number = 8, referenceDate: string) {
    if (e2eJacanaMock.isEnabled()) {
      return e2eJacanaMock.getPastOutings(limitCount, referenceDate);
    }

    const q = query(
      collection(db, OUTINGS_COLLECTION),
      where("date", "<", referenceDate),
      orderBy("date", "desc"),
      limit(limitCount)
    );
    const snapshot = await getDocs(q);
    const outings = snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() } as Outing));

    // Filter out deleted outings client-side
    return outings.filter(outing => !(outing as any).deleted);
  }
};

export const bookingsService = {
  async getByOuting(outingId: string) {
    if (e2eJacanaMock.isEnabled()) {
      return e2eJacanaMock.getBookingsByOuting(outingId);
    }

    const q = query(collection(db, BOOKINGS_COLLECTION), where("outingId", "==", outingId));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Booking));
  },

  async getAll() {
    if (e2eJacanaMock.isEnabled()) {
      return sortBookingsByCreatedAt(e2eJacanaMock.getAllBookings());
    }

    const q = query(collection(db, BOOKINGS_COLLECTION), orderBy("createdAt", "desc"));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Booking));
  },

  async getByUserEmail(email: string) {
    const normalizedEmail = email.trim().toLowerCase();

    if (e2eJacanaMock.isEnabled()) {
      return sortBookingsByCreatedAt(e2eJacanaMock.getBookingsByUserEmail(normalizedEmail));
    }

    const q = query(collection(db, BOOKINGS_COLLECTION), where("userEmail", "==", normalizedEmail));
    const snapshot = await getDocs(q);
    const bookings = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Booking));
    return sortBookingsByCreatedAt(bookings);
  },

  async getById(id: string) {
    if (e2eJacanaMock.isEnabled()) {
      return e2eJacanaMock.getBookingById(id);
    }

    const docRef = doc(db, BOOKINGS_COLLECTION, id);
    const snapshot = await getDoc(docRef);
    if (!snapshot.exists()) return null;
    return { id: snapshot.id, ...snapshot.data() } as Booking;
  },

  async updateStatus(id: string, status: string, isPaid: boolean) {
    // Compatibilité temporaire pour l'UI existante : les mutations critiques passent désormais par le backend.
    if (isPaid) {
      return bookingActionsService.validateManualPayment({ bookingId: id });
    }

    if (status === 'cancelled') {
      return bookingActionsService.cancelBooking({ bookingId: id });
    }

    throw new Error("Cette mutation directe est dépréciée. Utilisez une action métier explicite.");
  }
};
